
    import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
    import dayjs from 'dayjs';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import {
        faChevronRight as farChevronRight,
    } from '@fortawesome/pro-regular-svg-icons/faChevronRight';

    import {
        faChevronLeft as farChevronLeft,
    } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';

    import {
        faInfoCircle as farInfoCircle,
    } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';

    import { jobData } from '@/utils/jobData';
    import datalayer from '@/utils/datalayer';

    export default {
        components: {
            FontAwesomeIcon,
        },

        data() {
            return {
                icons: {
                    farChevronRight,
                    farChevronLeft,
                    farInfoCircle,
                },
            };
        },

        computed: {
            ...mapState({
                $_me: state => state.me.user,
                $_job: state => state.job.job,
                $_jobApplication: state => state.jobApplication,
                $_scoringData: state => state.me.scoringData,
                $_tracking: state => state.eventTracking,
            }),

            ...mapGetters('jobApplication', {
                $_isApplyTypeGuest: 'isApplyTypeGuest',
                $_isApplyTypeNew: 'isApplyTypeNew',
            }),

            ...mapGetters('me', {
                $_isOnboarded: 'isOnboarded',
            }),

            ...mapGetters('jobModal', {
                $_isJobModalOpen: 'isJobModalOpen',
            }),

            isModal() {
                return this.$_isJobModalOpen;
            },

            successApplyRedirectUrl() {
                return `/discover?uuid=${this.$_job.uuid}`;
            },

            getOffsiteApplyUrl() {
                return `${this.$siteUrl}job-redirect/${this.$_job.uuid}?flow=${this.$_job.flow_enabled === 'FLOW'}&token=${this.$_job.token}&source='button&page_referral_element=${this.$_tracking.page_referral_element}`;
            },
        },

        methods: {
            ...mapMutations('me', {
                $_PUT_JOB_EVENT: 'PUT_JOB_EVENT',
            }),

            ...mapMutations('job', {
                $_UPDATE_JOB: 'UPDATE_JOB',
            }),

            ...mapActions('jobApplication', {
                $_getJobQuestions: 'getJobQuestions',
            }),

            ...mapMutations('jobApplication', {
                $_APPLY: 'APPLY',
                $_STORE_APPLY_UUID: 'STORE_APPLY_UUID',
                $_STORE_APPLY_PASS_ACTIVATION_ID: 'STORE_APPLY_PASS_ACTIVATION_ID',
                $_CLOSE_APPLY_FORM: 'CLOSE_APPLY_FORM',
                $_CLOSE_APPLY_OFFSITE_FORM: 'CLOSE_APPLY_OFFSITE_FORM',
                $_OPEN_CUSTOM_QUESTIONS: 'OPEN_CUSTOM_QUESTIONS',
                $_OPEN_ONBOARDING_QUESTIONS: 'OPEN_ONBOARDING_QUESTIONS',
                $_OPEN_SIGNIN_MODAL: 'OPEN_SIGNIN_MODAL',
            }),

            ...mapActions('eventTracking', {
                $_pushEvent: 'pushEvent',
                $_pushSeekerJobEvent: 'pushSeekerJobEvent',
            }),

            async afterApply(application) {
                this.$_STORE_APPLY_UUID(application.uuid);
                this.$_STORE_APPLY_PASS_ACTIVATION_ID(application.applyPassActivationId);
                this.$_PUT_JOB_EVENT({ job: this.$_job.uuid, status: 'is_applied' });
                this.$_UPDATE_JOB({ key: 'has_applied', value: true });
                this.$_UPDATE_JOB({ key: 'is_complete_apply', value: true });
                this.$_UPDATE_JOB({ key: 'applied_at', value: dayjs().format('YYYY-MM-DD HH:mm:ss') });

                if (parseInt(this.$_job.apply_questions_count)) {
                    await this.openCustomQuestionForm();
                } else if (!this.$_isOnboarded && this.$_me.id) {
                    this.fireCompleteApplyEvent();
                    this.fireApplyPassEvent();
                    this.openOnboardingForm();
                } else {
                    this.completeApply();
                }
            },

            completeApply() {
                this.$_UPDATE_JOB({ key: 'applied_at', value: dayjs().format('YYYY-MM-DD HH:mm:ss') });
                this.$_APPLY();

                this.fireCompleteApplyEvent();

                this.afterCompleteApply();

                this.fireApplyPassEvent();
            },

            fireApplyPassEvent() {
                const data = {
                    event: 'apply_pass_activation',
                    apply_pass_id: this.$_jobApplication.apply_pass_activation_id,
                };

                if (this.$_jobApplication.apply_pass_activation_id) {
                    this.$_pushEvent(data);
                    datalayer(data);
                }
            },

            fireCompleteApplyEvent() {
                const data = {
                    event: 'job_apply_complete',
                    job_id: this.$_job.id,
                    job_uuid: this.$_job.uuid,
                    apply_uuid: this.$_jobApplication.apply_uuid,
                    seeker_status: this.$_isApplyTypeNew ? 'new' : 'existing',
                };

                this.$_pushEvent({
                    ...data,
                    ...jobData(this.$_job),
                });
                this.$_pushSeekerJobEvent({
                    ...data,
                    ...jobData(this.$_job),
                });
                datalayer(data);
            },

            eventJobApplyStart() {
                const data2 = {
                    event: 'job_apply_start',
                    site_position: 'job_apply',
                    seeker_match_score: this.$_job.seeker_match_score,
                    seeker_data: this.$_scoringData,
                    ...jobData(this.$_job),
                };
                datalayer(data2);
                this.$_pushEvent(data2);
                this.$_pushSeekerJobEvent(data2);
            },

            eventJobApplyAuthImpression() {
                const data = {
                    event: 'job_apply_auth_impression',
                    site_position: 'job_apply',
                    ...jobData(this.$_job),
                };
                datalayer(data);
                this.$_pushEvent(data);
            },

            openCustomQuestionForm() {
                return this.$_getJobQuestions({ jobUuid: this.$_job.uuid, applicationUuId: this.$_jobApplication.apply_uuid }).then(() => {
                    if (this.$_jobApplication.questions.length) {
                        this.$_OPEN_CUSTOM_QUESTIONS();
                    }
                }).catch(() => {
                    this.afterCompleteApply();
                });
            },

            afterCompleteApply() {
                window.scrollTo(0, 0);
            },

            openOnboardingForm() {
                this.$_APPLY();
                this.$_OPEN_ONBOARDING_QUESTIONS();
            },

            scroll() {
                if (['xl', 'inf'].includes(this.$mq)) {
                    this.scrollToApply();
                }
            },

            scrollToApply() {
                let targetDiv = document.getElementById('jvApply');

                const options = {
                    duration: 600,
                };

                if (this.isModal) {
                    const modalDiv = document.getElementById('jvModalBody');
                    targetDiv = modalDiv.querySelector('#jvApply');
                    options.begin = () => {
                        modalDiv.scrollTop = 0;
                    };
                    options.container = modalDiv;
                    options.offset = modalDiv.offsetTop;
                }

                this.$velocity(
                    targetDiv,
                    'scroll',
                    options,
                );
            },
        },
    };
